import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Button,
  Col,
  Container,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";
import i18n from "../../i18n";

// Import images
import natanelLg from "../../assets/images/logo/natanel-lg.png";

import {
  AuthUserInfo,
  CompanyId,
  EnabledModules,
  JobRole,
  USER_ROLE,
  UserId,
} from "../../constants";
import { changeAppLanguage } from "../../helpers";
import LocalstorageService from "../../helpers/localstorage-service";
import AuthService from "../../services/auth";
import CompanyService from "../../services/company";

import { setAuthToken, setAuthUser } from "../../store/slices/Auth/authSlice";

import FooterInscription from "../../components/FooterInscription";
import PasswordField from "../../components/PasswordField";
import SelectField from "../../components/SelectField";

const LoginSchema = Yup.object().shape({
  email: Yup.string().required(t("REQUIRED")),
  password: Yup.string().required(t("REQUIRED")),
});

const InitCompValues = {
  companyId: "",
};

const CompanyValidationSchema = Yup.object().shape({
  companyId: Yup.string().required(t("REQUIRED")),
});

let loginData = {};

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [companyOptList] = useState([]);
  const [isOpened, setIsOpened] = useState(false);
  let companyName = "";

  async function getCompanyFn() {
    await CompanyService.listCompany()
      .then((response) => {
        const { dataList } = response.data;

        if (dataList && dataList.length) {
          dataList.forEach((item, index) => {
            companyOptList[index] = {
              id: item.id,
              label: item.companyName,
              value: item.id,
            };
          });
        }
      })
      .finally(() => { });
  }


  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(false);

    setIsLoading(true);
    await AuthService.login({ ...values })
      .then(async (response) => {
        const { status, message, data, token } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        if (data) {
          loginData = { data, message, token };

          // Support both PascalCase (RoleId) and camelCase (roleId)
          const userRoleId = data.RoleId || data.roleId;

          // ✅ חשוב! שמירת Token מיידית כדי שבקשות הבאות יכללו אותו
          // נדרש במיוחד עבור SuperAdmin שקורא לרשימת חברות מיד אחרי Login
          LocalstorageService.setItem(AuthUserInfo, {
            data: data,
            token: token,
          });

          if (userRoleId === USER_ROLE.SUPER_ADMIN) {
            await getCompanyFn();
          }

          if (
            userRoleId === USER_ROLE.SUPER_ADMIN &&
            companyOptList.length !== 0
          ) {
            setIsOpened(true);
          } else {
            await handleLogin();
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // טעינת קודי הפרימיום הפעילים לחברה הנבחרת — ל-gating בתפריט/routes.
  // נכשל בשקט (שומר רשימה ריקה = בסיס בלבד) כדי לא לחסום login.
  async function loadEnabledModules() {
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      if (!companyId || companyId === 0) {
        LocalstorageService.setItem(EnabledModules, []);
        return;
      }
      const res = await CompanyService.getCompanyModules({ companyId, activeOnly: true });
      const list = res?.data?.dataList || [];
      const codes = list.filter((m) => m.isActive).map((m) => m.moduleCode);
      LocalstorageService.setItem(EnabledModules, codes);
    } catch (e) {
      console.warn("Failed to load enabled modules:", e);
      LocalstorageService.setItem(EnabledModules, []);
    }
  }

  const handleLogin = async () => {
    const { message, token, data } = loginData;

    // Map PascalCase fields from backend to camelCase for frontend
    const payload = {
      userName: data.UserName || data.userName,
      firstName: data.FirstName || data.firstName,
      lastName: data.LastName || data.lastName,
      knownAs: data.KnownAs || data.knownAs,
      mail: data.Mail || data.mail,
      company: data.Company || data.company,
    };

    // Support both PascalCase (RoleId) and camelCase (roleId)
    const userRoleId = data.RoleId || data.roleId;

    if (
      !!(userRoleId === USER_ROLE.SUPER_ADMIN && companyOptList.length !== 0)
    ) {
      payload.company = companyName;
    }
    dispatch(setAuthUser(payload));
    dispatch(setAuthToken(token));

    LocalstorageService.setItem(AuthUserInfo, {
      data: payload,
      token,
    });

    // ✅ ALWAYS save these critical fields!
    // IMPORTANT: Backend returns PascalCase (CompanyId, RoleId, Id)
    LocalstorageService.setItem(JobRole, data.RoleId || data.roleId);
    LocalstorageService.setItem(UserId, data.Id || data.id);

    // ✅ Save CompanyId for all users (not just non-super-admins)
    if (data.CompanyId || data.companyId) {
      LocalstorageService.setItem(CompanyId, data.CompanyId || data.companyId);
    }

    // טעינת המודולים הפעילים לפני הניווט (כדי שהתפריט יקרא אותם ב-render הראשון)
    await loadEnabledModules();

    toast.success(message);
    navigate("/dashboard");
  };

  // item A: SuperAdmin נכנס לחברה — מנפיק JWT חדש scoped (claim ActingAsCompany) ומחליף את טוקן
  // ה-login לטוקן החדש (handleLogin שומר אותו ומנווט). כשל ב-select-company לא חוסם כניסה —
  // נמשיך עם הטוקן המקורי (SA pass-through עדיין עובד לפי companyId מ-localStorage).
  const handleCompanySelect = async (values) => {
    const companyId = values?.companyId || LocalstorageService.getItem(CompanyId);
    try {
      const res = await AuthService.selectCompany({ companyId });
      const newToken = res?.data?.token;
      if (newToken) {
        loginData = { ...loginData, token: newToken };
      }
    } catch (e) {
      console.warn("select-company failed, continuing with original token:", e);
    }
    await handleLogin();
  };

  return (
    <React.Fragment>
      <div>
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4}>
              <div className="authentication-page-content p-4 d-flex align-items-center min-vh-100">
                <div className="w-100">
                  <Row className="justify-content-center">
                    <Col lg={10}>
                      <div>
                        <div className="text-center">
                          <div>
                            <img
                              src={natanelLg}
                              alt=""
                              height="30"
                              className="auth-logo logo-dark mx-auto"
                            />
                          </div>

                          <h4 className="font-size-18 mt-4">
                            {t("LOGIN.WELCOME_BACK")}
                          </h4>
                          <p className="text-muted">
                            {t("LOGIN.SIGN_IN_TO_CONTINUE") + " "}
                            {t("APPLICATION_TITLE")}.
                          </p>
                        </div>

                        <div className="p-2 mt-5">
                          <Formik
                            className="form-horizontal"
                            initialValues={{ email: "", password: "" }}
                            validationSchema={LoginSchema}
                            onSubmit={handleSubmit}
                          >
                            {(props) => {
                              const { isSubmitting, setFieldValue } = props;

                              return (
                                <Form>
                                  {/* Email */}
                                  <div className="mb-4">
                                    <div className="auth-form-group-custom">
                                      <i className="ri-user-2-line auti-custom-input-icon"></i>
                                      <Label htmlFor="email">
                                        {t("EMAIL_LABEL")}&nbsp;/&nbsp;
                                        {t("USERNAME_LABEL")}
                                      </Label>
                                      <Field
                                        name="email"
                                        type="text"
                                        className="form-control"
                                        id="email"
                                        placeholder={`${t(
                                          "EMAIL_PLACEHOLDER"
                                        )} / ${t(
                                          "USERNAME_LABEL"
                                        )?.toLowerCase()}`}
                                      />
                                    </div>

                                    <ErrorMessage
                                      name="email"
                                      className="text-danger"
                                      component="div"
                                    />
                                  </div>

                                  {/* Password */}
                                  <div className="mb-4">
                                    <PasswordField
                                      setFieldValue={setFieldValue}
                                    />
                                  </div>

                                  {/* Submit Button */}
                                  <div className="mt-4 text-center">
                                    <Button
                                      color="primary"
                                      className="w-md waves-effect waves-light"
                                      type="submit"
                                      disabled={isSubmitting || isLoading}
                                    >
                                      {isSubmitting || isLoading
                                        ? t("PLEASE_WAIT")
                                        : t("LOGIN.TITLE")}
                                    </Button>
                                  </div>

                                  {/* Forgot Password */}
                                  <div className="mt-4 text-center">
                                    <Link
                                      to="/forgot-password"
                                      className="text-muted"
                                    >
                                      <i className="mdi mdi-lock me-1"></i>{" "}
                                      {t("FORGOT_PASSWORD.TITLE")}
                                    </Link>
                                  </div>
                                </Form>
                              );
                            }}
                          </Formik>
                        </div>

                        {/* Offered Language */}
                        <div className="mt-3 text-center">
                          <strong>{t("OFFERED_IN")} : </strong>

                          {i18n.resolvedLanguage === "en" ? (
                            <Link onClick={() => changeAppLanguage("he")}>
                              <strong>עברית</strong>
                            </Link>
                          ) : (
                            <Link onClick={() => changeAppLanguage()}>
                              <strong>English</strong>
                            </Link>
                          )}
                        </div>

                        <div className="mt-3 text-center">
                          <FooterInscription />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            {/* Overlay */}
            <Col lg={8}>
              <div className="authentication-bg">
                <div className="bg-overlay"></div>
              </div>
            </Col>
          </Row>
        </Container>

        <Modal
          isOpen={isOpened}
          backdrop="static"
          className="modal-sm modal-dialog-centered"
        >
          <ModalHeader toggle={() => setIsOpened(false)}>
            {t("SELECT_COMPANY")}
          </ModalHeader>

          <ModalBody>
            <Formik
              initialValues={InitCompValues}
              enableReinitialize={true}
              validationSchema={CompanyValidationSchema}
              onSubmit={handleCompanySelect}
            >
              {({ values, isSubmitting, setFieldValue, setFieldTouched }) => (
                <Form noValidate>
                  <div className="mb-4">
                    <SelectField
                      fieldName="companyId"
                      fieldValue={values.companyId}
                      fieldOptions={companyOptList}
                      fieldChange={(option) => {
                        if (option) {
                          companyName = option.label;
                          LocalstorageService.setItem(CompanyId, option.value);
                        }
                      }}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      disabledOptVal="0"
                    />
                  </div>

                  {/* Select Company Button */}
                  <div className="mt-3">
                    <Button
                      type="submit"
                      color="primary"
                      className="w-100 waves-effect waves-light py-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t("PLEASE_WAIT") : t("CONFIRM")}
                    </Button>
                  </div>

                  {/* Separator */}
                  <hr style={{ margin: "24px 0", borderColor: "#ccc" }} />

                  {/* System Management Button */}
                  <div>
                    <Button
                      type="button"
                      color="warning"
                      className="w-100 waves-effect waves-light py-2"
                      onClick={() => {
                        // Save auth info before navigating to sysadmin
                        const { data, token } = loginData;
                        const userRoleId = data.RoleId || data.roleId;
                        LocalstorageService.setItem(JobRole, userRoleId);
                        LocalstorageService.setItem(UserId, data.Id || data.id);
                        if (data.CompanyId || data.companyId) {
                          LocalstorageService.setItem(CompanyId, data.CompanyId || data.companyId);
                        }
                        dispatch(setAuthToken(token));
                        dispatch(setAuthUser({
                          userName: data.UserName || data.userName,
                          firstName: data.FirstName || data.firstName,
                          lastName: data.LastName || data.lastName,
                          knownAs: data.KnownAs || data.knownAs,
                          mail: data.Mail || data.mail,
                          company: data.Company || data.company,
                        }));
                        navigate('/sysadmin/dashboard');
                      }}
                    >
                      <i className="ri-shield-star-line me-1"></i>
                      {t("SYSADMIN.ENTER_MANAGEMENT") || "כניסה לניהול מערכת"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </Modal>

      </div>
    </React.Fragment>
  );
}

export default Login;